import { Entry } from '../../db/DB';
import type { Replicant } from '../../ReplicantConfig';
import {
    FullSharedState,
    generateSharedStateId,
    PostSharedStatesMessagesMap,
    SharedState,
    SharedStateCountersAPI,
    SharedStatesAsyncActionAPI,
} from '../../ReplicantSharedStates';
import { APIAccessInterface } from '../../server/ServerReplicant';
import { generateMessageId } from '../../utils/MessageUtils';
import { DelayedAction } from '../ReplicantAPI';

function createCountersApi<T extends SharedState>(opts: {
    delayedActions: DelayedAction[];
    sharedState: T;
    stateName: string;
}): SharedStateCountersAPI<T> {
    const { delayedActions, sharedState, stateName } = opts;

    const api: SharedStateCountersAPI<any> = {};

    const counterNames = sharedState.schema.global?.counters ?? [];

    function tryValidateCounterValue(val: number, counter: string): void {
        const isValid = Number.isSafeInteger(val);

        if (!isValid) {
            throw Error(`Invalid shared state counter value in ${stateName}.${counter}: ${val}`);
        }
    }

    for (const counter of counterNames) {
        api[counter] = {
            increase(stateId, increment) {
                tryValidateCounterValue(increment, counter);

                delayedActions.push({ type: 'increaseSharedStateCounter', counter, increment, stateId, stateName });
            },

            set(stateId, value) {
                tryValidateCounterValue(value, counter);

                delayedActions.push({ type: 'setSharedStateCounter', counter, value, stateId, stateName });
            },
        };
    }

    return api as SharedStateCountersAPI<T>;
}

function createPostMessageMap<TSharedState extends SharedState>(opts: {
    async: boolean | undefined;
    dateNow: () => number;
    delayedActions: DelayedAction[];
    getClockOffset: () => number;
    sharedState: TSharedState;
    sharedStateName: string;
    userId: string;
}): PostSharedStatesMessagesMap<TSharedState['messages']> {
    const result: PostSharedStatesMessagesMap<any> = {};

    for (const messageName in opts.sharedState.messages) {
        result[messageName] = (stateId, payload) => {
            const messageId = generateMessageId(opts.dateNow() + opts.getClockOffset(), opts.delayedActions.length);

            opts.delayedActions.push({
                type: 'postSharedStateMessage',
                async: !!opts.async,
                message: {
                    id: messageId,
                    args: payload,
                    sender: opts.userId,
                    name: messageName,
                    senderClockOffset: opts.getClockOffset(),
                },
                stateId,
                stateName: opts.sharedStateName,
            });
        };
    }

    return result as PostSharedStatesMessagesMap<TSharedState['messages']>;
}

export function createSharedStatesAPI<T extends Replicant>(opts: {
    async: boolean | undefined;
    apiAccess: APIAccessInterface<T>;
    consistentFetchIds: readonly string[];
    dateNow: () => number;
    delayedActions: DelayedAction[];
    entry: Entry<T['state']>;
    getClockOffset: () => number;
    random: () => number;
    sharedStates: T['sharedStates'] | undefined;
    userId: string;
}): SharedStatesAsyncActionAPI<T['sharedStates']> {
    if (!opts.sharedStates) {
        return {} as SharedStatesAsyncActionAPI<T['sharedStates']>;
    }

    const { entry, random, sharedStates, userId } = opts;
    const result = {} as SharedStatesAsyncActionAPI<T['sharedStates']>;

    for (const key in sharedStates) {
        const stateName = key as keyof T['sharedStates'] & string;
        const sharedState: SharedState = sharedStates[stateName]! as any;

        result[stateName] = {
            counters: createCountersApi({ delayedActions: opts.delayedActions, sharedState, stateName }),

            create: (customSharedStateId) => {
                if (customSharedStateId === '') {
                    throw Error(`Invalid custom shared state ID: cannot be the empty string`);
                }

                const stateId =
                    customSharedStateId ?? generateSharedStateId({ userId, now: opts.dateNow, random, stateName });

                opts.delayedActions.push({ type: 'createSharedState', stateId, stateName });

                return stateId;
            },

            fetch: async (stateId) => {
                const consistentRead = opts.consistentFetchIds.includes(stateId);

                const { sharedState } = await opts.apiAccess.sharedStates.fetchSharedState({
                    stateName,
                    stateId,
                    consistentRead,
                });

                if (!sharedState) {
                    return undefined;
                }

                return sharedState as FullSharedState<any>;
            },

            fetchOrCreate: async (stateId) => {
                const sharedState = await opts.apiAccess.sharedStates.fetchOrCreateSharedState({
                    stateId,
                    stateName,
                });

                return sharedState as FullSharedState<any>;
            },

            setUserState: (stateId, state) => {
                const oldUserState = entry.userSharedStates[stateName]?.[stateId]?.state;

                const newUserState = opts.apiAccess.sharedStates.setUserState({
                    entry,
                    state,
                    stateName,
                    stateId,
                    userId,
                });

                opts.delayedActions.push({
                    type: 'setUserSharedState',
                    previousStateValue: oldUserState ? JSON.stringify(oldUserState) : undefined,
                    stateId,
                    stateName,
                    userState: newUserState,
                });
            },

            deleteUserState: (stateId) => {
                if (!opts.sharedStates?.[stateName]) {
                    throw Error(`Cannot delete user shared state value for ${stateName}: invalid shared state name.`);
                }

                if (!entry.userSharedStates[stateName]?.[stateId]) {
                    throw Error(
                        `Cannot delete user shared state value for ${stateName} ${stateId}: user shared state does not exist.`,
                    );
                }

                delete entry.userSharedStates[stateName]![stateId];

                opts.delayedActions.push({
                    type: 'deleteUserSharedState',
                    stateId,
                    stateName,
                    userId,
                });
            },

            postMessage: createPostMessageMap({ ...opts, sharedState, sharedStateName: stateName }) as any,

            count: (query) => opts.apiAccess.countSharedStates(stateName, query),
            search: (query) => opts.apiAccess.searchSharedStates(stateName, query),
        };
    }

    return result;
}
